import { MouseEvent, useMemo, useState, useContext, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useTheme,
  Typography,
  Divider,
} from "@mui/material";
import useTable from "~src/hooks/useTable";
import ReactCountryFlag from "react-country-flag";
import { AdData, MapOfBrands, ProductData } from "~src/types/brandMapping";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Iconify from "~src/components/Iconify";
import { BrandComparisonContext } from "~src/contexts/BrandComparisonContext";
import { TableSkeleton } from "~src/components/table";
import { trackBrand } from "~src/api/brands";
import { PATH_BRANDS } from "~src/routes/paths";
import { BrandResult } from "~src/sections/competitorAnalytics/SearchBar";
import TableHeader from "./TableHeader";
import AdImageSlider from "./ImgaeSlider/AdImageSlider";
import ProductImageSlider from "./ImgaeSlider/ProductImageSlider";
import { dateFormatter } from "../utils";
import BottomScrollListener from "~src/components/BottomScrollListener";
import BrandAvatar from "~src/components/BrandAvatar";

enum Headers {
  BRAND = "Brand",
  MATCHING_ADS = "Matching Ads",
  MATCHING_PRODUCTS = "Matching Products",
  SELLING_FOR = "Selling for",
  TOTAL_PRODUCTS = "Total Products",
  COUNTRY = "Country",
  PUBLISHED_ON = "Published on",
}

type PropsType = {
  tabVersion: string;
  setBrandToRemove?: React.Dispatch<React.SetStateAction<MapOfBrands[] | undefined>>;
  mappedBrands: MapOfBrands[] | undefined;
  brandConfig: (brand: MapOfBrands) => BrandConfigResult;
  setClickedBrand: React.Dispatch<React.SetStateAction<MapOfBrands | undefined>>;
  clickedBrand: any;
  expandAdImage: (data: AdData, brand: MapOfBrands) => void;
  expandProductImage: (data: ProductData, brand: MapOfBrands) => void;
  setAdImagePopup: React.Dispatch<React.SetStateAction<boolean>>;
  loading: boolean;
  loadMore: () => void;
};

type PriceRange = {
  min: number;
  max: number | string;
};

type BrandConfigResult = {
  productImageUrls: ProductData[];
  adImageUrls: AdData[];
  priceRange: PriceRange;
};

export const MapOfBrandList = ({
                                 tabVersion,
                                 setBrandToRemove,
                                 mappedBrands,
                                 brandConfig,
                                 clickedBrand,
                                 setClickedBrand,
                                 expandAdImage,
                                 expandProductImage,
                                 loading,
                                 loadMore,
                               }: PropsType) => {
  const theme = useTheme();
  const { dense } = useTable();
  const tableHeaders = useMemo(() => Object.values(Headers), []);
  const { selectedBrands, setSelectedBrands } = useContext(BrandComparisonContext) || {};

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Wrap handleClose in useCallback to keep its reference stable.
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLElement>, brand: any) => {
      setAnchorEl(event.currentTarget);
      setClickedBrand(brand);
    },
    [setClickedBrand]
  );

  const handleTrackStore = useCallback(() => {
    if (clickedBrand) {
      trackBrand(clickedBrand.brand.brand_id, true);
    }
    handleClose();
  }, [clickedBrand, handleClose]);

  const handleAddBrand = useCallback(() => {
    if (clickedBrand && setSelectedBrands) {
      setSelectedBrands((previousBrands) => {
        const brandExists = previousBrands.some(
          (b) => b.id === String(clickedBrand.brand.brand_id)
        );
        if (brandExists) {
          return previousBrands;
        } else {
          const newBrand: BrandResult = {
            id: String(clickedBrand.brand.brand_id),
            brand_name: clickedBrand.brand.brandname,
            image: clickedBrand.brand.profileimage,
          };
          return [...previousBrands, newBrand];
        }
      });
    }
    handleClose();
  }, [clickedBrand, setSelectedBrands, handleClose]);

  const handleExceptBrand = useCallback(() => {
    if (clickedBrand && setBrandToRemove) {
      setBrandToRemove((prevBrands) => {
        const currentBrands = prevBrands ?? [];
        return [...currentBrands, clickedBrand];
      });
    }
  }, [clickedBrand, setBrandToRemove]);

  return (
    <Box
      sx={{
        backgroundColor: "background.default",
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: "12px",
        mt: 3,
        color: theme.palette.grey[200],
      }}
    >
      <TableContainer sx={{ position: "relative" }}>
        <Table size={dense ? "small" : "medium"}>
          <TableHeader headLabel={tableHeaders} />
          <TableBody>
            {mappedBrands?.map((brand, index) => {
              const { productImageUrls, adImageUrls, priceRange } = brandConfig(brand);
              const brandIdToCheck = String(brand.brand.brand_id);
              const isBrandSelected = selectedBrands?.some(
                (brand) => brand.id === brandIdToCheck
              );
              return (
                <TableRow
                  key={brand.brand.brand_id + index}
                  sx={{
                    backgroundColor: "background.default",
                    border: `1px solid ${theme.palette.grey[200]}`,
                    borderRadius: "12px",
                  }}
                >
                  <TableCell sx={{ cursor: "pointer" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <IconButton
                        id="basic-button"
                        aria-controls={open ? "basic-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? "true" : undefined}
                        onClick={(event) => handleClick(event, brand)}
                      >
                        <Iconify
                          icon={"iconamoon:menu-kebab-vertical-bold"}
                          width={20}
                          height={20}
                        />
                      </IconButton>
                      <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        PaperProps={{
                          sx: {
                            boxShadow: "none",
                            border: `1px solid ${theme.palette.grey[200]}`,
                          },
                        }}
                        slotProps={{
                          list: { "aria-labelledby": "basic-button" }
                        }}
                      >
                        <MenuItem onClick={handleTrackStore}>
                          <Iconify
                            icon={"ph:heart"}
                            sx={{ mr: 1 }}
                            width={20}
                            height={20}
                          />{" "}
                          Track Store
                        </MenuItem>
                        {!isBrandSelected && (
                          <Box>
                            <Divider
                              sx={{
                                mx: 1,
                                borderColor: theme.palette.grey[200],
                              }}
                            />
                            <MenuItem onClick={handleAddBrand}>
                              <Iconify
                                icon={"pajamas:comparison"}
                                sx={{ mr: 1 }}
                                width={20}
                                height={20}
                              />{" "}
                              Add for Comparison
                            </MenuItem>
                          </Box>
                        )}
                        {(tabVersion === "v3" || tabVersion === "v2") && (
                          <Box>
                            <Divider
                              sx={{
                                mx: 1,
                                borderColor: theme.palette.grey[200],
                              }}
                            />
                            <MenuItem onClick={handleExceptBrand}>
                              <Iconify
                                icon={"clarity:remove-solid"}
                                sx={{ mr: 1 }}
                                width={20}
                                height={20}
                              />{" "}
                              Exclude Brand
                            </MenuItem>
                          </Box>
                        )}
                      </Menu>
                      <Box
                        sx={{ display: "flex", alignItems: "center" }}
                        onClick={() =>
                          window.open(
                            PATH_BRANDS.overview(brand.brand.brand_id),
                            "_blank"
                          )
                        }
                      >
                        <BrandAvatar
                          brandName={brand.brand.brandname}
                          profileImage={brand.brand.profileimage}
                          size={40}
                          sx={{
                            mr: 2,
                          }}
                        />
                        <Typography variant="subtitle2">
                          {brand.brand.brandname}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {adImageUrls.length > 0 ? (
                      <AdImageSlider
                        data={adImageUrls}
                        brand={brand}
                        expandImage={expandAdImage}
                      />
                    ) : (
                      <Box
                        sx={{
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                        }}
                      >
                        <Iconify
                          icon={"raphael:no"}
                          sx={{ fontSize: 24, mr: 1 }}
                        />
                        <Typography variant="body2">
                          No Matching Ads
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {productImageUrls.length > 0 ? (
                      <ProductImageSlider
                        data={productImageUrls}
                        brand={brand}
                        expandImage={expandProductImage}
                      />
                    ) : (
                      <Box
                        sx={{
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                        }}
                      >
                        <Iconify
                          icon={"raphael:no"}
                          sx={{ fontSize: 24, mr: 1 }}
                        />
                        <Typography variant="body2">
                          No Matching Products
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ cursor: "pointer", maxWidth: "160px" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="subtitle2" noWrap>
                        ${priceRange.min}-{priceRange.max}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ maxWidth: "120px" }}>
                    <Typography variant="subtitle2" noWrap>
                      {brand.total_product_count || 0}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ cursor: "pointer" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <ReactCountryFlag
                        countryCode={brand.brand.country}
                        svg
                        style={{
                          width: "2em",
                          height: "2em",
                        }}
                        title={brand.brand.country}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ maxWidth: "120px" }}>
                    <Typography variant="subtitle2" noWrap>
                      {dateFormatter(brand.published_date)}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
            {loading && (
              <Box>
                {Array.from({ length: 6 }).map((_, index) => (
                  <TableSkeleton key={index} />
                ))}
              </Box>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <BottomScrollListener onBottom={loadMore} offset={200} />
    </Box>
  );
};
